import { useAccount } from "$src/stores/use-account";
import { useFilters } from "$src/stores/use-filters";
import { cx } from "$src/lib/utils";
import { getTeamBySlug } from "$src/lib/teams";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import styles from "./brand-dropdown.module.css";

type BrandDropdownProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export const BrandDropdown = ({
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}: BrandDropdownProps = {}) => {
  const account = useAccount((s) => s.active);
  const accounts = useAccount((s) => s.accounts);
  const activate = useAccount((s) => s.activate);
  const resetFilters = useFilters((s) => s.reset);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const teamSlug = pathname.split("/")[1] || "";
  const team = getTeamBySlug(teamSlug);

  const filtered = useMemo(() => {
    if (!search.trim()) return accounts;
    const query = search.toLowerCase();
    return accounts.filter(
      (acc) =>
        acc.brandName.toLowerCase().includes(query) ||
        acc.accountName.toLowerCase().includes(query),
    );
  }, [accounts, search]);

  const handleSelect = (accountBrandId: number) => {
    if (accountBrandId === account?.accountBrandId) {
      setOpen(false);
      setSearch("");
      return;
    }
    resetFilters();
    activate(accountBrandId);
    setOpen(false);
    setSearch("");
  };

  if (!account) return null;

  return (
    <>
      {!hideTrigger && (
        <button
          className={cx(styles.trigger, open && styles["trigger-open"])}
          onClick={() => setOpen(!open)}
        >
          <span className={styles["brand-name"]}>{account.brandName}</span>
          <span className={styles["brand-meta"]}>
            {account.category.name}, {account.geography.name}
          </span>
        </button>
      )}

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              className={styles.backdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setOpen(false);
                setSearch("");
              }}
            />

            {/* Modal */}
            <motion.div
              className={styles.modal}
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              {/* Brand Selector */}
              <div className={styles.section}>
                <label className={styles.label}>Brand</label>
                <div className={styles.select}>
                  <span className={styles["select-text"]}>
                    {account.brandName}
                  </span>
                  <ChevronDown size={16} className={styles["select-chevron"]} />
                </div>
              </div>

              {/* Search */}
              <div className={styles.section}>
                <label className={styles.label}>Search</label>
                <input
                  type="text"
                  className={styles.input}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search brands..."
                />
              </div>

              {/* Brand List */}
              <div className={styles.list}>
                {filtered.map((item) => (
                  <button
                    key={item.accountBrandId}
                    className={cx(
                      styles.listItem,
                      item.accountBrandId === account.accountBrandId &&
                        styles["listItem-active"],
                    )}
                    onClick={() => handleSelect(item.accountBrandId)}
                  >
                    {item.brandName}
                  </button>
                ))}
                {filtered.length === 0 && (
                  <div className={styles.empty}>No brands found</div>
                )}
              </div>

              {/* Category & Geography */}
              <div className={styles.section}>
                <label className={styles.label}>Category</label>
                <div className={styles.field}>{account.category.name}</div>
              </div>

              <div className={styles.section}>
                <label className={styles.label}>Geography</label>
                <div className={styles.field}>{account.geography.name}</div>
              </div>

              {/* Change Account Button */}
              <button className={styles.button}>Change account</button>

              {/* Change Team */}
              <button
                className={styles.changeTeam}
                onClick={() => { navigate("/"); setOpen(false); setSearch(""); }}
              >
                <Users size={15} />
                <span>Change team</span>
                {team && <span className={styles.changeTeamCurrent}>{team.name}</span>}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
